import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type Document,
  type SubmissionDocumentsResponse,
  fetchSubmissionDocuments,
  submissionDocumentsQueryKey,
  updateDocumentCategory,
} from './submissionDocumentApi';

export type { Document, SubmissionDocumentsResponse };
export { updateDocumentCategory };

interface UseSubmissionDocumentsReturn {
  documents: Document[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  clearError: () => void;
}

export const useSubmissionDocuments = (
  submissionId: string | null
): UseSubmissionDocumentsReturn => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: submissionDocumentsQueryKey(submissionId ?? ''),
    queryFn: () => fetchSubmissionDocuments(submissionId!),
    enabled: !!submissionId,
  });

  const invalidate = useCallback(async () => {
    if (!submissionId) return;
    await queryClient.invalidateQueries({
      queryKey: submissionDocumentsQueryKey(submissionId),
    });
  }, [queryClient, submissionId]);

  const clearError = useCallback(() => {}, []);

  return {
    documents: data ?? [],
    isLoading,
    error: error instanceof Error ? error.message : null,
    refetch: invalidate,
    clearError,
  };
};
